import React, { useEffect, useState, useMemo } from "react";
import {
  Card, Table, Badge, Form, Row, Col, Button,
  InputGroup, Pagination
} from "react-bootstrap";
import {
  LuSearch, LuTag, LuChevronLeft, LuChevronRight,
  LuCircleDot, LuTrash2
} from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCategory,
  getCategoryList,
} from "../../../redux/reducers/productSlice";
import AddProductCategory from "./AddProductCategory";
import ConfirmModal from "../../../components/ConfirmModal";

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 15, 20];

export default function ProductCategoryList() {
  const dispatch = useDispatch();

  const selectedBusiness = useSelector((state) => state.business.selectedBusiness);
  const productcategorydata = useSelector((state) => state.product.categoryList);

  useEffect(() => {
    if (selectedBusiness?._id) {
      dispatch(getCategoryList(selectedBusiness._id));
    }
  }, [dispatch, selectedBusiness]);

  // --- FILTER STATE ---
  const [searchQuery, setSearchQuery] = useState("");

  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // --- DELETE STATE ---
  const [deletemodalshow, setDeletemodalshow] = useState(false);
  const [deleteid, setDeleteid] = useState(null);

  // --- DERIVED: filtered list ---
  const filteredCategories = useMemo(() => {
    if (!productcategorydata) return [];
    return productcategorydata.filter((cat) =>
      cat.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [productcategorydata, searchQuery]);

  // --- PAGINATION ---
  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / limit));

  const paginatedCategories = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return filteredCategories.slice(start, start + limit);
  }, [filteredCategories, currentPage, limit]);

  // Reset to page 1 on filter/limit change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, limit]);

  // --- DELETE ---
  const handleDelete = () => {
    dispatch(deleteCategory(deleteid)).then((res) => {
      if (res.payload.status !== "error") {
        setDeletemodalshow(false);
      }
    });
  };

  return (
    <div className="mt-4">

      {/* --- FILTER BAR --- */}
      <Card className="border-0 shadow-sm rounded-4 p-3 mb-4">
        <Row className="g-3 align-items-end">

          <Col lg={4} md={6}>
            <Form.Label className="small fw-bold text-muted mb-1">Search</Form.Label>
            <InputGroup className="bg-light rounded-pill overflow-hidden border-0">
              <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                <LuSearch size={16} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search category..."
                className="bg-transparent border-0 shadow-none py-2 small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Col>

          {searchQuery && (
            <Col lg="auto">
              <Button
                variant="outline-secondary"
                className="rounded-pill px-3 small fw-bold py-2"
                onClick={() => setSearchQuery("")}
              >
                Clear
              </Button>
            </Col>
          )}

          <Col lg="auto" className="ms-auto">
            <div className="text-muted small fw-medium text-end">
              <strong>{filteredCategories.length}</strong> of{" "}
              <strong>{productcategorydata?.length ?? 0}</strong> categories
            </div>
          </Col>

        </Row>
      </Card>

      {/* --- TABLE --- */}
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle mb-0">
            <thead className="bg-light text-muted small text-uppercase">
              <tr>
                <th className="ps-4 py-3 fw-semibold">Category</th>
                <th className="fw-semibold">Products</th>
                <th className="pe-4 text-end fw-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCategories.length > 0 ? (
                paginatedCategories.map((category) => (
                  <tr key={category._id}>

                    {/* Category */}
                    <td className="ps-4 py-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="p-2 bg-light rounded-3 text-muted">
                          <LuTag size={18} />
                        </div>
                        <div>
                          <div className="fw-bold small">{category?.name}</div>
                          <div className="text-muted" style={{ fontSize: "11px" }}>
                            ID: #{category?._id?.slice(-6).toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Product count badge — if available on your data */}
                    <td>
                      {category?.productCount != null ? (
                        <Badge
                          bg="light"
                          className="text-dark border rounded-pill fw-normal px-2 py-1 small"
                        >
                          {category.productCount} product{category.productCount !== 1 ? "s" : ""}
                        </Badge>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end align-items-center gap-2">
                        <AddProductCategory type="update" data={category} />
                        <Button
                          variant="light"
                          size="sm"
                          className="rounded-circle p-2"
                          title="Delete category"
                          onClick={() => {
                            setDeleteid(category?._id);
                            setDeletemodalshow(true);
                          }}
                        >
                          <LuTrash2 size={15} className="text-danger" />
                        </Button>
                      </div>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center py-5 text-muted">
                    <LuCircleDot size={40} className="mb-2 opacity-25" />
                    <p className="mb-0 small">
                      {searchQuery
                        ? "No categories match your search."
                        : "No categories found."}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>

        {/* --- PAGINATION FOOTER --- */}
        {filteredCategories.length > 0 && (
          <Card.Footer className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted fw-medium">Rows per page:</small>
                <Form.Select
                  size="sm"
                  className="border-0 bg-light rounded-3 fw-bold py-1 px-2"
                  style={{ width: "70px", fontSize: "0.8rem" }}
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Form.Select>
              </div>
              <small className="text-muted fw-medium border-start ps-3">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                &nbsp;({filteredCategories.length} total)
              </small>
            </div>

            <Pagination
              className="mb-0 gap-1"
              style={{
                "--bs-pagination-active-bg": "#28B0C4",
                "--bs-pagination-active-border-color": "#28B0C4",
              }}
            >
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="border-0 shadow-none"
              >
                <LuChevronLeft />
              </Pagination.Prev>
              {[...Array(totalPages)].map((_, idx) => (
                <Pagination.Item
                  key={idx + 1}
                  active={idx + 1 === currentPage}
                  onClick={() => setCurrentPage(idx + 1)}
                >
                  {idx + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="border-0 shadow-none"
              >
                <LuChevronRight />
              </Pagination.Next>
            </Pagination>
          </Card.Footer>
        )}
      </Card>

      {/* --- DELETE MODAL --- */}
      <ConfirmModal
        show={deletemodalshow}
        onHide={() => setDeletemodalshow(false)}
        onConfirm={handleDelete}
        title="Delete Category"
        message="Are you sure you want to delete this category? This action is permanent and cannot be undone."
        confirmText="Delete Category"
        variant="danger"
        icon={LuTrash2}
      />
    </div>
  );
}